import React from "react";
import { motion } from "framer-motion";
import { FaTooth, FaShieldAlt, FaUserMd, FaSmile, FaMicroscope } from "react-icons/fa";
import aboutImage from "../assets/aboutimage.png";

export default function About() {
  const features = [
    { 
      icon: <FaTooth className="text-2xl sm:text-3xl" />, 
      title: "Modern Technology", 
      desc: "Digital equipment for accurate diagnosis and advanced dental treatments." 
    },
    { 
      icon: <FaShieldAlt className="text-2xl sm:text-3xl" />, 
      title: "Painless Care", 
      desc: "Comfortable and gentle procedures focused on patient care." 
    },
    { 
      icon: <FaUserMd className="text-2xl sm:text-3xl" />, 
      title: "Experienced Dentist", 
      desc: "Skilled dental professional delivering personalized treatment." 
    },
    { 
      icon: <FaSmile className="text-2xl sm:text-3xl" />, 
      title: "Affordable Care", 
      desc: "Quality dental treatments at transparent and reasonable prices." 
    },
  ];

  const stats = [
    { value: "10+", label: "Years Experience" },
    { value: "5000+", label: "Happy Patients" },
    { value: "100%", label: "Patient Satisfaction" },
  ];

  return (
    <section id="about" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-white relative overflow-hidden">
      
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal-50/40 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-teal-50/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-teal-100"
          >
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="text-teal-600 text-xs sm:text-sm font-semibold tracking-wide">ABOUT US</span>
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1E293B] mb-4"
          >
            About <span className="bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] bg-clip-text text-transparent">Dr. Sainulabid's IQRA Multispeciality Dental Clinic</span>
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            whileInView={{ opacity: 1, width: "5rem" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] mx-auto rounded-full"
          />
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto mt-4"
          >
            Committed to excellence in dental care with modern technology and compassionate service
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-600 leading-relaxed mb-4 text-sm sm:text-base">
              <span className="font-semibold text-[#2CA6B1]">Dr. Sainulabid's IQRA Multispeciality Dental Clinic</span> is a trusted dental clinic in <span className="font-semibold">Athavanad, Malappuram</span>, providing comprehensive dental care for children, adults and families. Our clinic combines experienced dental care, modern technology and patient-focused treatment to deliver safe, comfortable and affordable dental care.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-4 text-sm sm:text-base">
              We provide a complete range of dental treatments including <span className="font-semibold text-teal-600">Dental Implants</span>, <span className="font-semibold text-teal-600">Root Canal Treatment</span>, Teeth Cleaning &amp; Scaling, Dental Fillings, Tooth Extraction, Crowns &amp; Bridges, Braces &amp; Aligners, Cosmetic Dentistry, Smile Designing and preventive dental care. Every treatment is performed with advanced equipment while maintaining the highest standards of hygiene, safety and patient comfort.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6 text-sm sm:text-base">
              Our goal is to help every patient achieve a healthy and confident smile through quality treatment, transparent communication and compassionate care. Whether you need a routine dental check-up or advanced dental treatment, our experienced team is here to provide the best possible care for you and your family.
            </p>

            <p className="text-gray-500 text-xs sm:text-sm leading-relaxed mb-6 bg-teal-50/50 p-3 sm:p-4 rounded-xl border border-teal-100">
              <span className="font-semibold text-teal-600">📍 We proudly welcome patients</span> from Athavanad, Kattilangadi, Kurumbathur, Valanchery, Kuttippuram, Pattarnadakavu, Thirunavaya, Puthanathani and surrounding areas across Malappuram.
            </p>

            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
              {stats.map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-3 sm:p-4 bg-gradient-to-br from-teal-50 to-white rounded-xl shadow-sm hover:shadow-md transition-all"
                >
                  <div className="text-xl sm:text-2xl font-bold text-[#2CA6B1]">{stat.value}</div>
                  <div className="text-[10px] sm:text-xs text-gray-500">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden"
                >
                  <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-teal-50/50 to-white border border-teal-100/50 hover:border-teal-200 transition-all duration-300 cursor-pointer hover:shadow-lg">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                    />
                    <div className="text-[#2CA6B1] group-hover:scale-110 transition-transform duration-300 relative z-10">
                      {feature.icon}
                    </div>
                    <div className="relative z-10">
                      <h4 className="font-semibold text-[#1E293B] text-sm sm:text-base">{feature.title}</h4>
                      <p className="text-[10px] sm:text-xs text-gray-500">{feature.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative group"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.02, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity"
            />
            
            <div className="relative bg-gradient-to-br from-white to-teal-50/30 p-2 rounded-2xl shadow-xl backdrop-blur-sm border border-white/50">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={aboutImage}
                  alt="Dr. Sainulabid's IQRA Multispeciality Dental Clinic in Athavanad, Malappuram"
                  className="rounded-xl w-full object-cover h-64 sm:h-80 md:h-96 transition-transform duration-700 group-hover:scale-105"
                />
                <motion.div
                  animate={{ x: ["-100%", "100%"] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
                  style={{ width: "50%", filter: "blur(10px)" }}
                />
              </div>
            </div>
            
            <motion.div
              animate={{ 
                y: [0, -8, 0],
                rotate: [0, 5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 bg-white rounded-xl shadow-xl p-2 sm:p-3"
            >
              <div className="bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] text-white px-2 sm:px-3 py-1 rounded-lg text-[10px] sm:text-sm font-semibold">
                Est. 2015
              </div>
            </motion.div>

            <motion.div
              animate={{ 
                y: [0, -5, 0],
                x: [0, 5, 0]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-2 sm:p-3 border border-teal-100"
            >
              <div className="flex items-center gap-2">
                <FaMicroscope className="text-[#2CA6B1] text-sm sm:text-base" />
                <div>
                  <div className="font-bold text-[#1E293B] text-xs sm:text-sm">Advanced Care</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}